import { useEffect, useRef } from 'react';

import type { ListFilterFieldDef, ListSelectorFilterDef } from '../model/types';
import type { ListPageController } from '../model/useListPageController';
import { encodeDisplayPayload } from './payload';
import type { ListPageServerState } from './useListPageServerState';

function safeId<TItem>(def: ListSelectorFilterDef<TItem>, item: unknown): string | null {
  try {
    return String(def.getId(item as TItem));
  } catch {
    return null; // Ref v zastaralém tvaru
  }
}

/** Normalizace pro porovnání: items v pořadí ids (JSON deep-compare). */
function normalizedJson<TItem>(
  def: ListSelectorFilterDef<TItem>,
  ids: string[],
  items: unknown[]
): string {
  const byId = new Map(
    items
      .map((item) => [safeId(def, item), item] as const)
      .filter((pair): pair is readonly [string, unknown] => pair[0] !== null)
  );
  return JSON.stringify(ids.map((id) => byId.get(id) ?? null));
}

/**
 * Background revalidace uložených *Ref modelů selector filtrů (žádná rehydratace v UI):
 * po aplikaci pohledu se na pozadí ověří aktuální podoba záznamů přes `resolveByIds`;
 * při rozdílu se items tiše vymění in-memory (bez dirty, bez nového dotazu) a u serverového
 * pohledu se přeuloží DisplayPayload — smí kdokoliv, definice ani autor se nemění.
 * Smazané záznamy si podrží poslední známý Ref (label místo syrového id).
 */
export function useSelectorRefRevalidation<TItem>({
  controller,
  server,
  defs,
}: {
  controller: ListPageController<TItem>;
  server: ListPageServerState;
  defs: Map<string, ListFilterFieldDef>;
}) {
  const revalidatedViewId = useRef<string | null>(null);

  // Aktuální filtry přes ref — revalidace se spouští jen změnou pohledu, ne každou změnou filtru.
  const filtersRef = useRef(controller.filters);
  filtersRef.current = controller.filters;
  const controllerRef = useRef(controller);
  controllerRef.current = controller;

  useEffect(() => {
    const viewId = controller.activeViewId;
    if (revalidatedViewId.current === viewId) {
      return;
    }
    revalidatedViewId.current = viewId;

    const isServerView = !viewId.startsWith('~');
    let cancelled = false;
    // StrictMode-safe: zrušený běh (double-effect v dev) vrátí guard, ať revalidace
    // proběhne v následném běhu; dokončené porovnání je idempotentní (zápis jen při rozdílu).
    const releaseGuardOnCancel = () => {
      cancelled = true;
      if (revalidatedViewId.current === viewId) {
        revalidatedViewId.current = null;
      }
    };

    for (const filter of filtersRef.current) {
      const def = defs.get(filter.field);
      if (def?.type !== 'selector' || !filter.ids || filter.ids.length === 0) {
        continue;
      }
      const selectorDef = def;
      const ids = [...filter.ids];
      const resolveByIds = selectorDef.selector.createOptions().resolveByIds;
      if (!resolveByIds) {
        continue;
      }

      void resolveByIds(ids, {})
        .then((resolved) => {
          if (cancelled) {
            return;
          }
          const freshById = new Map(
            resolved
              .map((item) => [safeId(selectorDef, item), item] as const)
              .filter((pair): pair is readonly [string, unknown] => pair[0] !== null)
          );
          const storedById = new Map(
            (filter.items ?? [])
              .map((item) => [safeId(selectorDef, item), item] as const)
              .filter((pair): pair is readonly [string, unknown] => pair[0] !== null)
          );
          // Čerstvý Ref má přednost; smazaný záznam si podrží poslední známý.
          const nextItems = ids
            .map((id) => freshById.get(id) ?? storedById.get(id))
            .filter((item): item is unknown => item !== undefined);

          if (
            normalizedJson(selectorDef, ids, nextItems) ===
            normalizedJson(selectorDef, ids, filter.items ?? [])
          ) {
            return; // beze změny — žádný render skok ani zápis
          }

          controllerRef.current.refreshFilterItems(filter.field, nextItems);

          if (isServerView) {
            const refreshedFilters = filtersRef.current.map((f) =>
              f.field === filter.field ? { ...f, items: nextItems } : f
            );
            const displayPayload = encodeDisplayPayload(refreshedFilters, defs);
            if (displayPayload) {
              server.setViewDisplay(viewId, displayPayload);
            }
          }
        })
        .catch(() => {
          // revalidace je best-effort — selhání nechává uložené labely beze změny
        });
    }

    return releaseGuardOnCancel;
  }, [controller.activeViewId, defs, server]);
}
